import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Search, SearchX, X } from "lucide-react";
import { useDistribution } from "@/hooks/useDistribution";
import { PAGE_SIZE, useQuestions } from "@/hooks/useQuestions";
import { errorMessage } from "@/lib/api";
import { nf } from "@/lib/format";
import { L } from "@/i18n";
import { ModuleNav } from "@/components/questions/ModuleNav";
import { QuestionCard } from "@/components/questions/QuestionCard";
import { QuestionsPager } from "@/components/questions/QuestionsPager";
import { EditQuestionDialog } from "@/components/questions/EditQuestionDialog";

const findCount = (modules, sel, total) => {
  if (sel === "all") return { count: total, label: L("All questions", "Todas as perguntas") };
  for (const m of modules) {
    if (m.id === sel) return { count: m.count, label: `${m.id} · ${m.name}` };
    const s = m.submodules?.find((x) => x.id === sel);
    if (s) return { count: s.count, label: `${s.id} · ${s.name}` };
  }
  return { count: null, label: sel };
};

export default function Questions() {
  const dist = useDistribution();
  const [module, setModule] = useState("all");
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const q = useQuestions({ module, search, page });

  useEffect(() => {
    const t = setTimeout(() => setSearch(input.trim()), 400);
    return () => clearTimeout(t);
  }, [input]);
  useEffect(() => setPage(1), [module, search]);

  const modules = dist.data?.modules ?? [];
  const info = useMemo(() => findCount(modules, module, dist.data?.total_questions), [modules, module, dist.data]);
  const pages = !search && info.count ? Math.ceil(info.count / PAGE_SIZE) : null;
  const items = q.data ?? [];

  const select = (id) => {
    setModule(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="questions-page">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">{L("Catalog", "Catálogo")}</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">{L("Questions M1–M17", "Perguntas M1–M17")}</h1>
      <p className="mt-2 mb-8 text-sm text-slate-400" data-testid="questions-total">
        {dist.data ? L("{n} questions across {m} modules", "{n} perguntas em {m} módulos", { n: nf.format(dist.data.total_questions), m: modules.length }) : L("Loading catalog…", "A carregar catálogo…")}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 items-start">
        <aside className="rounded-lg bg-[#131f37] border border-[#1e293b] p-3 lg:sticky lg:top-24 max-h-80 lg:max-h-[calc(100vh-8rem)] overflow-y-auto">
          {dist.isLoading ? <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-amber-500" /> : (
            <ModuleNav modules={modules} total={dist.data?.total_questions ?? 0} selected={module} onSelect={select} />
          )}
        </aside>

        <section className="min-w-0 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={L("Search question text…", "Pesquisar no texto das perguntas…")}
              className="w-full h-11 rounded-md bg-[#0f172a] border border-slate-700 pl-10 pr-9 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-colors"
              data-testid="questions-search-input"
            />
            {input && (
              <button onClick={() => setInput("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200" data-testid="questions-search-clear" aria-label={L("Clear", "Limpar")}>
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between text-xs text-slate-400">
            <span data-testid="questions-selected-label"><b className="text-slate-200">{info.label}</b>{info.count != null && !search && ` · ${L("{n} questions", "{n} perguntas", { n: nf.format(info.count) })}`}{search && ` · ${L("search", "pesquisa")} "${search}"`}</span>
            {q.isFetching && <Loader2 className="h-4 w-4 animate-spin text-amber-500" />}
          </div>

          {q.isError && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-6 text-center text-sm text-rose-300" data-testid="questions-error">
              <AlertTriangle className="mx-auto mb-2 h-5 w-5" /> {errorMessage(q.error)}
            </div>
          )}
          {q.isSuccess && items.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-700 p-12 text-center" data-testid="questions-empty">
              <SearchX className="mx-auto h-6 w-6 text-slate-500" />
              <p className="mt-3 text-sm text-slate-400">{L("No questions found.", "Nenhuma pergunta encontrada.")}</p>
            </div>
          )}
          <div className={`space-y-4 transition-opacity ${q.isPlaceholderData ? "opacity-60" : ""}`} data-testid="questions-list">
            {items.map((item, i) => <QuestionCard key={item.id} q={item} index={(page - 1) * PAGE_SIZE + i + 1} onEdit={setEditing} />)}
          </div>
          {items.length > 0 && (
            <QuestionsPager page={page} pages={pages} hasNext={items.length === PAGE_SIZE && (!pages || page < pages)} onPage={(p) => { setPage(p); window.scrollTo({ top: 0 }); }} />
          )}
        </section>
      </div>
      <EditQuestionDialog question={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
